import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService } from '@Common';
import { AuthnService } from '@Common';
import { BreadcrumbsService } from '@Common';
import { WebAnalyticsService } from '@Common';
import { LocalizedTextIds, UserRoles } from 'company-finder-common';
import { KeyValue } from '@angular/common';
import { getParsedReturnUrl } from '@Common';
import { ComponentBase } from '@Common';
import { DeploymentContext } from '@Common';
import { SearchResolverResult } from '@Common';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	standalone: false,
})
export class LoginComponent extends ComponentBase implements OnInit {
	public username: string;
	public password: string;
	public authenticationMessage: string;
	public userRole: KeyValue<UserRoles, string>;
	public company: KeyValue<string, string>;

	public userRoles = UserRoles;
	public companies: KeyValue<string, string>[] = [];
	public isDebug = false;

	constructor(
		dc: DeploymentContext,
		private _authnService: AuthnService,
		private _breadcrumbsService: BreadcrumbsService,
		private _router: Router,
		private _webAnalyticsService: WebAnalyticsService,
		private userService: UserService,
		private activatedRoute: ActivatedRoute,
		private _activatedRoute: ActivatedRoute
	) {
		super(dc);
		this.isDebug = !!activatedRoute.snapshot.url.find(
			(segment) => segment.path.toLocaleLowerCase() === 'debug'
		);
		this.userRole = {
			key: UserRoles.Internal,
			value: UserRoles.Internal.toString(),
		};
	}

	ngOnInit(): void {
		this._authnService.clearJnjLoggedOut();
		if (this.isDebug) {
			this.addSubscription(
				this._activatedRoute.data.subscribe(
					async (data: { searchResults: SearchResolverResult }) => {
						this.companies = data.searchResults.companies
							.map((company, idx) => ({
								key: idx.toString(),
								value: company.name,
							}))
							.sort((a, b) => a.value.localeCompare(b.value));
					}
				)
			);
		}
	}

	public async login(): Promise<void> {
		this.clearTokenAndUser();
		this.authenticationMessage = '';
		const username = this.username?.trim();

		await this._authnService.login(
			username,
			this.password,
			this.userRole?.value,
			this.company?.value
		);

		if (this._authnService.isAuthenticated) {
			const avoidInterpolationOfSensitiveVariablesBuildWarning =
				this.username;
			this._webAnalyticsService.trackEvent('login-via-login-form', {
				category: 'successful-login',
				label: `${avoidInterpolationOfSensitiveVariablesBuildWarning} - ${this.userRole?.value}`,
			});

			const target = getParsedReturnUrl(this.activatedRoute);
			this._router.navigateByUrl(target);
		} else {
			this.authenticationMessage = this.Loc(
				LocalizedTextIds.LoginJJNetwork
			);
		}
	}

	public get roleSupportsCompanyAssociation(): boolean {
		return [UserRoles.Company, UserRoles.InternalSuper].includes(
			this.userRole.key
		);
	}

	public getReferenceValueDisplayString(
		role: KeyValue<string, string>
	): string {
		return role?.value ?? '';
	}

	public logout(): void {
		this.clearTokenAndUser();
		this._router.navigate(['/']);
	}

	private clearTokenAndUser(): void {
		this._authnService.clearToken();
		this.userService.clearCachedUser();
	}
}
